import { storeAuthHeaders, resetAuthHeaders, getAuthHeaders } from '@/utils/authentication_headers';
import { handleError } from '@/utils/exception_handler';

import api from './api';

export default {

  // Uses FETCH mutations on USERS
  fetchUsers: (context, args = {}) => {
    const { commit, dispatch } = context;
    const mutationPrefix = 'fetchUsers';

    commit(`${mutationPrefix}Pending`);

    return api.fetchUsers(args)
      .then((response) => {
        dispatch('insert', { data: response.entities });

        commit(`${mutationPrefix}Success`, response.entities);

        return { status: true, data: response };
      }, handleError(context, `${mutationPrefix}Error`))
      .catch(handleError(context, `${mutationPrefix}Error`));
  },

  // Uses FETCH mutations on USERS
  fetchSingleUser: (context, args = {}) => {
    const { commit, dispatch } = context;
    const mutationPrefix = 'fetchSingleUser';

    commit(`${mutationPrefix}Pending`);

    return api.fetchSingleUser(args)
      .then((response) => {
        dispatch('insert', { data: response.entity });

        commit(`${mutationPrefix}Success`, response.entity);

        return { status: true, data: response };
      }, handleError(context, `${mutationPrefix}Error`))
      .catch(handleError(context, `${mutationPrefix}Error`));
  },

  // Uses CUSTOM mutations on USERS
  register: (context, data) => {
    const { commit } = context;
    const mutationPrefix = 'register';

    commit(`${mutationPrefix}Pending`);

    return api.register(data)
      .then((response) => {
        commit(`${mutationPrefix}Success`);

        return { status: true, response };
      }, handleError(context, `${mutationPrefix}Error`))
      .catch(handleError(context, `${mutationPrefix}Error`));
  },

  // Uses CUSTOM mutations on USERS
  login: (context, data) => {
    const { commit, dispatch } = context;
    const mutationPrefix = 'login';

    commit(`${mutationPrefix}Pending`, data);

    return api.login(data)
      .then((response) => {
        storeAuthHeaders(response);

        dispatch('insert', { data: response.entity });
        dispatch('setLocale', response.entity.locale, { root: true });

        commit(`${mutationPrefix}Success`, response.entity.id);

        return { status: true, data: response };
      }, handleError(context, `${mutationPrefix}Error`))
      .catch(handleError(context, `${mutationPrefix}Error`));
  },

  getCurrentUser: (context) => {
    const { commit, dispatch } = context;
    const mutationPrefix = 'getCurrentUser';

    commit(`${mutationPrefix}Pending`);

    return api.getCurrentUser()
      .then((response) => {
        dispatch('insert', { data: response.entity });
        dispatch('setLocale', response.entity.locale, { root: true });

        commit(`${mutationPrefix}Success`, response.entity.id);

        return { status: true, data: response };
      }, handleError(context, `${mutationPrefix}Error`))
      .catch(handleError(context, `${mutationPrefix}Error`));
  },

  // Uses CUSTOM mutations on USERS
  logout: (context) => {
    const { commit, dispatch, getters } = context;
    const userId = getters.currentUser.id;
    const mutationPrefix = 'logout';

    commit(`${mutationPrefix}Pending`, userId);

    return api.logout()
      .then((response) => {
        resetAuthHeaders();

        dispatch('delete', userId);
        commit(`${mutationPrefix}Success`, response);

        return { status: true, response };
      }, handleError(context, `${mutationPrefix}Error`, { id: userId }))
      .catch(handleError(context, `${mutationPrefix}Error`, { id: userId }));
  },

  // Uses CUSTOM mutations on USERS
  validateToken: (context) => {
    const { commit, dispatch } = context;
    const authHeaders = getAuthHeaders();
    const mutationPrefix = 'validateToken';
    const hasAuthHeaders = !!(authHeaders
      && authHeaders.uid
      && authHeaders.client
      && authHeaders['access-token']);

    commit(`${mutationPrefix}Pending`);

    if (!hasAuthHeaders) {
      resetAuthHeaders();
      commit(`${mutationPrefix}Error`);
      return Promise.resolve({ status: false, error: 'Missing auth headers' });
    }

    return api.validateToken(authHeaders)
      .then((response) => {
        storeAuthHeaders(response);

        dispatch('setLocale', response.entity.locale, { root: true });
        dispatch('insert', { data: response.entity });

        commit(`${mutationPrefix}Success`, response.entity.id);

        return { status: true, data: response };
      }, handleError(context, `${mutationPrefix}Error`, null, () => resetAuthHeaders()))
      .catch(handleError(context, `${mutationPrefix}Error`, null, () => resetAuthHeaders()));
  },

  // Uses CUSTOM mutations on USERS
  sendPasswordReset: (context, data) => {
    const { commit } = context;
    const mutationPrefix = 'sendPasswordReset';

    commit(`${mutationPrefix}Pending`);

    return api.sendPasswordReset(data)
      .then((response) => {
        commit(`${mutationPrefix}Success`);

        return { status: true, response };
      }, handleError(context, `${mutationPrefix}Error`))
      .catch(handleError(context, `${mutationPrefix}Error`));
  },

  // Uses CUSTOM mutations on USERS
  updatePassword: (context, requestParams) => {
    const { commit } = context;
    const mutationPrefix = 'updatePassword';

    commit(`${mutationPrefix}Pending`);

    return api.updatePassword(requestParams)
      .then((response) => {
        commit(`${mutationPrefix}Success`);

        return { status: true, response };
      }, handleError(context, `${mutationPrefix}Error`))
      .catch(handleError(context, `${mutationPrefix}Error`));
  },

  // Uses UPDATE mutations on USERS
  updateAccount: (context, requestParams) => {
    const { commit, dispatch, getters } = context;
    const userId = getters.currentUser.id;
    const mutationPrefix = 'updateAccount';

    commit(`${mutationPrefix}Pending`, userId);

    return api.updateAccount(requestParams)
      .then((response) => {
        dispatch('update', response.entity);
        dispatch('setLocale', response.entity.locale, { root: true });

        commit(`${mutationPrefix}Success`, userId);

        return { status: true, data: response };
      }, handleError(context, `${mutationPrefix}Error`, { id: userId }))
      .catch(handleError(context, `${mutationPrefix}Error`, { id: userId }));
  },

  // Uses CREATE mutations on USERS (done by admin)
  createUser: (context, args = {}) => {
    const { commit, dispatch } = context;
    const mutationPrefix = 'createUser';

    commit(`${mutationPrefix}Pending`);

    return api.createUser(args)
      .then((response) => {
        dispatch('insert', { data: response.entity });

        commit(`${mutationPrefix}Success`, response.entity);

        return { status: true, data: response };
      }, handleError(context, `${mutationPrefix}Error`))
      .catch(handleError(context, `${mutationPrefix}Error`));
  },

  // Uses UPDATE mutations on USERS (done by admin)
  updateUser: (context, requestParams) => {
    const { commit, dispatch } = context;
    const userId = requestParams.id;
    const mutationPrefix = 'updateUser';

    commit(`${mutationPrefix}Pending`, userId);

    return api.updateUser(requestParams)
      .then((response) => {
        dispatch('update', response.entity);

        commit(`${mutationPrefix}Success`, userId);

        return { status: true, data: response };
      }, handleError(context, `${mutationPrefix}Error`, { id: userId }))
      .catch(handleError(context, `${mutationPrefix}Error`, { id: userId }));
  },

  // Uses DELETE mutations on USERS (done by admin)
  deleteUser: (context, id) => {
    const { commit, dispatch } = context;
    const mutationPrefix = 'deleteUser';

    commit(`${mutationPrefix}Pending`, id);

    return api.deleteUser(id)
      .then((response) => {
        dispatch('delete', id);

        commit(`${mutationPrefix}Success`, id);

        return { status: true, data: response };
      }, handleError(context, `${mutationPrefix}Error`, { id }))
      .catch(handleError(context, `${mutationPrefix}Error`, { id }));
  },

  setContext: ({ commit }, payload = null) => {
    commit('setContext', payload);
  },

  updateCurrentUser: ({ commit }, payload = null) => {
    commit('updateCurrentUser', payload);
  },

  openFormModal: ({ commit }, payload = null) => {
    commit('openFormModal', payload);
  },

  closeFormModal: ({ commit }) => {
    commit('closeFormModal');
  },

};



// WEBPACK FOOTER //
// ./src/store/modules/user/actions.js